/**
 * Types and accessors shared by the browser shell's chrome and its surface host.
 *
 * These lived in `BrowserPreviewPanel.tsx` alongside 2,000 lines of component
 * code, where two of them had drifted into duplicates of each other
 * (`BrowserPreviewChromeRuntimeState` and `BrowserPreviewRuntimeState` were the
 * same `ReturnType`, declared 360 lines apart). Splitting the shell into files
 * required a single home for them, which is also the fix for that duplication.
 *
 * The accessors exist because `session` and `activeTab` are nullable everywhere
 * in the shell, and `?? null` repeated at forty call sites is where an
 * `undefined` eventually leaks into a prop typed `string | null`.
 *
 * @module browserShellTypes
 */
import type { BrowserSessionSnapshot, BrowserTabSnapshot } from "@cmmd-forge/contracts";

import type {
  buildBrowserOptionsMenuItems,
  deriveBrowserPreviewPanelRuntimeState,
  deriveBrowserPreviewPanelSessionState,
  resolveBrowserAccessTarget,
} from "../../components/BrowserPreviewPanel.logic";

export type BrowserPreviewSessionState = ReturnType<typeof deriveBrowserPreviewPanelSessionState>;
export type BrowserPreviewRuntimeState = ReturnType<typeof deriveBrowserPreviewPanelRuntimeState>;

/**
 * Where the human last pointed, per tab.
 *
 * A key press has no coordinates of its own, so it is delivered at the last
 * click point — that is what makes typing land in the field the user just
 * clicked. Tagged with the tab so a press can never be delivered at a point
 * measured in a different tab's viewport.
 */
export interface InteractivePointerPoint {
  readonly tabId: string;
  readonly x: number;
  readonly y: number;
}

export function browserPreviewTabId(
  tab: BrowserTabSnapshot | null,
): BrowserTabSnapshot["tabId"] | null {
  return tab?.tabId ?? null;
}

export function browserPreviewTabUrl(tab: BrowserTabSnapshot | null): string | null {
  return tab?.url ?? null;
}

export function browserPreviewBrowserId(
  session: BrowserSessionSnapshot | null,
): BrowserSessionSnapshot["browserId"] | null {
  return session?.browserId ?? null;
}

export function browserPreviewSessionId(
  session: BrowserSessionSnapshot | null,
): BrowserSessionSnapshot["sessionId"] | null {
  return session?.sessionId ?? null;
}

export function browserPreviewUserId(
  session: BrowserSessionSnapshot | null,
): BrowserSessionSnapshot["userId"] | null {
  return session?.userId ?? null;
}

/** How the active URL can be reached, and whether an external open is offered. */
export type BrowserPreviewAccessTarget = ReturnType<typeof resolveBrowserAccessTarget>;

export type BrowserOptionsMenuItem = ReturnType<typeof buildBrowserOptionsMenuItems>[number];

/**
 * Submit the address bar, either into the active tab or a new one.
 *
 * Returns a promise the caller may ignore: navigation is fire-and-forget from the
 * chrome's point of view, and the surface reflects the result.
 */
export type BrowserAddressSubmitter = (
  mode: "navigate-active-tab" | "new-tab",
  value?: string | null,
) => void | Promise<unknown>;
